import { ensureApiSessionReady } from '../../data/apiSession'
import { ServerApiClient } from '../../data/ServerApiClient'

/** The one public MCP endpoint every AI app connects to. */
export const MCP_URL = 'https://api.fusegrid.io/api/platform/mcp/v1'
/** Placeholder until FuseGrid for Excel is listed (C9 owns the listing). */
export const APPSOURCE_URL = 'https://appsource.microsoft.com/'
export const EXCEL_MANIFEST_PATH = '/excel-addin/manifest.xml'
export const EXCEL_ADMIN_DEPLOY_DOC = 'https://learn.microsoft.com/microsoft-365/admin/manage/manage-deployment-of-add-ins'
export const CLAUDE_CONNECTORS_URL = 'https://claude.ai/settings/connectors'
export const CLAUDE_CODE_COMMAND = `claude mcp add --transport http fusegrid ${MCP_URL}`
/** Placeholder until the ChatGPT app directory listing exists (C9 submits it). */
export const CHATGPT_DIRECTORY_URL = 'https://chatgpt.com/apps'
/**
 * "Book a working session". The web code had no booking link constant, so this opens a pre-addressed email to
 * the team; swap in the scheduling link when there is one.
 */
// Public Google Calendar appointment schedule ("30 min with Casey"); the same link the guided tours use.
export const BOOK_WORKING_SESSION_URL = 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ3qHItqtxh0s6HeunIixDI-0dFhUzmR1bxQEUGxhoEc6j2bTz-hGRJnULQ9pp-Gm7P6GM5wudXa?gv=true'

export interface ConnectionCompany { workbook_id: string; name: string }
export interface Connection {
  grant_id: string
  user_id: string
  user_email: string
  client_id: string
  client_name: string
  registration_kind: 'dcr' | 'cimd' | 'loopback_dcr'
  /** `all_accessible` follows the user's live access; `selected` lists `companies`. */
  company_mode: 'all_accessible' | 'selected'
  companies: ConnectionCompany[]
  scopes: string[]
  connected_at: string
  last_used_at: string | null
  refresh_expires_at: string | null
}
export type ChecklistStepKey = 'connect_books' | 'invite_team' | 'connect_ai' | 'install_excel' | 'first_playbook_demo' | 'first_playbook_real'
export interface ChecklistStep {
  step: ChecklistStepKey
  title: string
  done: boolean
  source: 'observed' | 'manual' | 'playbook' | null
  completed_at: string | null
}
export interface Checklist { steps: ChecklistStep[]; done: number; total: number }
export interface ActivityItem {
  kind: 'capability_call' | 'host_operation'
  id: string
  at: string
  via: string
  who: string | null
  client_name: string | null
  name: string
  status: string
  error_code: string | null
  summary: string
  receipt: unknown
}

export class ConnectApiError extends Error {
  readonly status: number
  readonly code: string
  constructor(status: number, code: string) {
    super(code)
    this.status = status
    this.code = code
  }
}

async function call<T>(path: string, init: RequestInit = {}, token?: string): Promise<T> {
  const bearer = token ?? (await ensureApiSessionReady()).token
  if (!bearer) throw new ConnectApiError(401, 'sign_in_required')
  const response = await fetch(`${new ServerApiClient().baseUrl}${path}`, {
    ...init,
    cache: 'no-store',
    headers: { Authorization: `Bearer ${bearer}`, ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...init.headers },
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) throw new ConnectApiError(response.status, String((body as { error?: unknown }).error || 'request_failed'))
  return body as T
}

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) })

export const connectApi = {
  myConnections: () => call<{ connections: Connection[] }>('/api/platform/oauth/connections').then(r => r.connections),
  narrowScopes: (c: Pick<Connection, 'grant_id'>, scopes: string[]) =>
    call<{ scopes: string[] }>('/api/platform/oauth/connections', json('PATCH', { grant_id: c.grant_id, scopes })),
  revokeMine: (c: Pick<Connection, 'grant_id'>) =>
    call<{ count: number }>('/api/platform/oauth/connections', json('DELETE', { grant_id: c.grant_id })),
  firmConnections: (siteId: string) =>
    call<{ connections: Connection[] }>(`/api/platform/sites/${encodeURIComponent(siteId)}/oauth/connections`).then(r => r.connections),
  revokeForMember: (siteId: string, c: Pick<Connection, 'grant_id'>) =>
    call<{ count: number }>(`/api/platform/sites/${encodeURIComponent(siteId)}/oauth/connections`, json('DELETE', { grant_id: c.grant_id })),
  checklist: () => call<Checklist>('/api/platform/getting-started'),
  markStep: (step: ChecklistStepKey, playbookId?: string) =>
    call<Checklist>(`/api/platform/getting-started/${step}`, json('POST', playbookId ? { playbook_id: playbookId } : {})),
  coachMode: () => call<{ coach_mode: boolean }>('/api/platform/preferences/ai').then(r => r.coach_mode),
  setCoachMode: (on: boolean) => call<{ coach_mode: boolean }>('/api/platform/preferences/ai', json('PUT', { coach_mode: on })).then(r => r.coach_mode),
  activity: (workbookId: string, limit = 50) =>
    call<{ items: ActivityItem[] }>(`/api/platform/companies/${encodeURIComponent(workbookId)}/ai-activity?limit=${limit}`).then(r => r.items),
  /** Practice mode: a zero-touch demo session held in memory only, so the advisor's own session is untouched. */
  demoSessionToken: async (): Promise<string> => {
    const response = await fetch(`${new ServerApiClient().baseUrl}/api/auth/demo-session`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}', cache: 'no-store' })
    const body = await response.json().catch(() => ({})) as { access_token?: string; error?: string }
    if (!response.ok || !body.access_token) throw new ConnectApiError(response.status, String(body.error || 'demo_unavailable'))
    return body.access_token
  },
  /** FE:C11 — catalog examples and playbooks ranked for what the advisor is doing now.
   *  `company` (a company key or workbook id) names the chips for that company when the caller can access it. */
  suggestions: (page: string, limit = 4, company?: string) =>
    call<SuggestionList>(`/api/capabilities/suggestions?${new URLSearchParams({ page, limit: String(limit), ...(company ? { company } : {}) })}`),
  /** FE:C11 — category and urgency labels for a "Get help" request; routing only, never an answer. */
  supportTriage: (text: string) => call<SupportTriage>('/api/capabilities/support-triage', json('POST', { text })),
  /** POST /api/capabilities/invoke (C2), with an explicit bearer for practice runs. */
  invoke: (capability: string, args: Record<string, unknown>, opts: { company?: string; token?: string } = {}) =>
    call<CapabilityResponse>('/api/capabilities/invoke', json('POST', { capability, arguments: args, company: opts.company, request_id: crypto.randomUUID() }), opts.token),
}

export interface Suggestion {
  kind: 'example' | 'playbook'
  id: string
  label: string
  capability: string | null
  score: number
}

export interface SuggestionList {
  suggestions: Suggestion[]
  ranker: 'jev' | 'lexical'
  /** Present when the requested company resolved against the caller's access. */
  company?: { company_key: string; display_name: string }
}

export interface SupportTriage {
  category: string
  urgency: number
  priority: 'urgent' | 'high' | 'normal'
  labels: string[]
  triaged_by: 'jev' | 'lexical'
}

export interface CapabilityResponse {
  ok: boolean
  result?: unknown
  proposed_call?: { name: string; arguments: unknown } | null
  error?: { code: string; message?: string } | null
}

export const SITE_ADMIN_ROLES = new Set(['site_admin_creator', 'site_admin_explorer'])

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

/** `/ai-activity?company=<workbook id>` → the workbook id. */
export const companyFromSearch = (search: string) => new URLSearchParams(search).get('company') || ''

/** Where the person came from: `/connect?company=<key or workbook id>&from=<path>` (CfoPageShell's Connect AI link). */
export function connectContext(search: string): { company: string | null; from: string } {
  const company = companyFromSearch(search).trim() || null
  const from = new URLSearchParams(search).get('from') || ''
  // Only a same-site path; anything else ("//host", a full URL) goes home.
  return { company, from: from.startsWith('/') && !from.startsWith('//') && !from.startsWith('/\\') ? from : '/' }
}
